import AuthLayout from "@/components/layout/AuthLayout"
import { FC, useEffect, useState } from "react"
import { confirmUser } from "@/shared/api"
import useQuery from "@/hooks/getQuery"
import { notification } from "antd"
import { Button, Result, Spin } from "antd"
import { NavLink, useNavigate } from "react-router-dom"

const ConfirmAccount: FC = () => {
  const [status, setStatus] = useState<'pending' | 'success' | 'error' | 'idle'>('idle')
  const [countdown, setCountdown] = useState(10)
  const query = useQuery()
  const token = query.get("token")
  const navigate = useNavigate()

  useEffect(() => {
    const confirmUserAccount = async () => {
      if (!token) {
        setStatus('error')
        return
      }
      
      setStatus('pending')
      try {
        console.log("Starting account confirmation with token:", token.substring(0, 15) + '...')
        const result = await confirmUser(token)
        console.log("Account confirmation successful:", result)
        localStorage.setItem('theme', 'light')
        setStatus('success')
      } catch (error) {
        console.error("Error confirming user account:", error)
        setStatus('error')
      }
    }

    // Begin confirmation process when component mounts
    confirmUserAccount()
  }, [token])

  // Countdown and auto-redirect on success
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)

      return () => clearTimeout(timer)
    } else if (status === 'success' && countdown === 0) {
      navigate('/sign-in')
    }
  }, [status, countdown, navigate])

  return (
    <AuthLayout title="Account Confirmation" subtitle="Confirming your Datacord account">
      {status === 'idle' || status === 'pending' ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', gap: '1rem' }}>
          <Spin size="large" />
          <p style={{ fontSize: '16px', color: '#666', marginTop: '1rem' }}>Verifying your account...</p>
          <p style={{ fontSize: '14px', color: '#999' }}>Please wait while we confirm your email address</p>
        </div>
      ) : status === 'success' ? (
        <Result
          status="success"
          title="🎉 Account Successfully Confirmed!"
          subTitle={
            <div style={{ fontSize: '16px' }}>
              <p style={{ marginBottom: '8px' }}>Your email has been verified and your account is now active.</p>
              <p style={{ fontWeight: 500, color: '#1890ff' }}>
                Redirecting to sign in page in {countdown} second{countdown !== 1 ? 's' : ''}...
              </p>
            </div>
          }
          extra={[
            <NavLink to="/sign-in" key="sign-in">
              <Button type="primary" size="large">
                Sign In Now
              </Button>
            </NavLink>
          ]}
        />
      ) : (
        <Result
          status="error"
          title="Account Confirmation Failed"
          subTitle={
            <div style={{ fontSize: '16px' }}>
              <p style={{ marginBottom: '8px' }}>We couldn't confirm your account.</p>
              <p style={{ color: '#666' }}>The confirmation link may have expired or is invalid.</p>
              <p style={{ marginTop: '16px', color: '#999' }}>Please try signing in or contact support if the issue persists.</p>
            </div>
          }
          extra={[
            <NavLink to="/sign-in" key="sign-in">
              <Button type="primary" size="large">
                Go to Sign In
              </Button>
            </NavLink>,
            <NavLink to="/sign-up" key="sign-up">
              <Button size="large">
                Create New Account
              </Button>
            </NavLink>
          ]}
        />
      )}
    </AuthLayout>
  )
}

export default ConfirmAccount 